import type { IBillingResourceUsage } from '../types';
import { toNullableNumber, toNumber } from './numbers';

export type ResourceHealth = 'healthy' | 'warning' | 'critical' | 'unlimited';

/**
 * Warn threshold used when the plan doesn't configure a `softLimitPercent` —
 * the 70% rung of the §8 alert ladder (70 "using most" · 85 "getting close" ·
 * 100 "used up").
 */
export const WARNING_RATIO = 0.7;
/** The ladder's middle rung — fixed by spec, not plan-configurable. */
export const NEAR_LIMIT_RATIO = 0.85;

/** Escalation rung for alert copy; null = below every rung. */
export type ResourceAlertTier = 70 | 85 | 100 | null;

export interface IResourceComputed {
    readonly used: number;
    readonly reserved: number;
    readonly addOnCapacity: number;
    /** Total available (included + add-ons); null = unlimited. */
    readonly limit: number | null;
    /** Subscription-included portion (limit − add-ons); null = unlimited. */
    readonly included: number | null;
    readonly remaining: number | null;
    /** used / limit; 0 when unlimited. */
    readonly ratio: number;
    readonly percentUsed: number;
    /** Warn threshold actually applied (plan's, or the 70% fallback). */
    readonly softLimitPercent: number;
    readonly health: ResourceHealth;
    /** §8 ladder rung for alert copy — 70 / 85 / 100, null below the ladder. */
    readonly alertTier: ResourceAlertTier;
    /**
     * Credit-pool view of a resource with no plan cap (SMS, email, voice):
     * consumed + what is left in the pool. Null when not pool-metered. Kept apart
     * from `limit` so calling and sending gates, which read `limit`, are unaffected.
     */
    readonly poolTotal: number | null;
    /** Credits left in the pool; null when not pool-metered. */
    readonly poolRemaining: number | null;
}

/**
 * The plan's own warn threshold, as a ratio. Anything absent or out of range
 * falls back to `WARNING_RATIO` so a malformed plan can't silence the warning
 * (0%) or make it unreachable (>100%).
 */
function toSoftLimitRatio(softLimitPercent: number | null | undefined): number {
    if (softLimitPercent == null || !Number.isFinite(softLimitPercent)) return WARNING_RATIO;
    if (softLimitPercent <= 0 || softLimitPercent > 100) return WARNING_RATIO;
    return softLimitPercent / 100;
}

/**
 * One derivation for every allocation view (cards, tables, chips, alerts) —
 * so the app-shell banner, a page's usage chip and the billing meter can
 * never disagree about whether a resource is warning, critical or fine.
 *
 * `health` is a pure function of the CURRENT snapshot: usage that drops back
 * under the threshold reads `healthy` again and the warning surfaces vanish
 * on their own.
 */
export function computeResource(resource: IBillingResourceUsage): IResourceComputed {
    const used = toNumber(resource.used);
    const reserved = toNumber(resource.reserved);
    const addOnCapacity = toNumber(resource.addOnCapacity);
    const limit = resource.isUnlimited ? null : toNullableNumber(resource.limit);
    const included = limit === null ? null : Math.max(0, limit - addOnCapacity);
    const remaining =
        limit === null
            ? null
            : (toNullableNumber(resource.remaining) ?? Math.max(0, limit - used - reserved));
    const ratio = limit !== null && limit > 0 ? used / limit : 0;
    const poolRemaining =
        limit === null && resource.poolAvailable !== undefined
            ? Math.max(0, toNumber(resource.poolAvailable))
            : null;
    const poolTotal = poolRemaining === null ? null : used + poolRemaining;
    const percentUsed = Math.round(
        (poolTotal !== null && poolTotal > 0 ? used / poolTotal : ratio) * 100,
    );
    const softLimitRatio = toSoftLimitRatio(resource.softLimitPercent);
    const health: ResourceHealth =
        limit === null
            ? 'unlimited'
            : ratio >= 1
              ? 'critical'
              : ratio >= softLimitRatio
                ? 'warning'
                : 'healthy';
    // The 85/100 rungs are fixed by spec; the 70 rung respects a plan's own
    // (higher) soft limit so a plan that deliberately warns late stays quiet.
    const alertTier: ResourceAlertTier =
        limit === null
            ? null
            : ratio >= 1
              ? 100
              : ratio >= NEAR_LIMIT_RATIO
                ? 85
                : ratio >= softLimitRatio
                  ? 70
                  : null;

    return {
        used,
        reserved,
        addOnCapacity,
        limit,
        included,
        remaining,
        ratio,
        percentUsed,
        softLimitPercent: Math.round(softLimitRatio * 100),
        health,
        alertTier,
        poolTotal,
        poolRemaining,
    };
}

/**
 * Meter fill width (0–100). Unlimited meters read full; a metered resource
 * with any usage shows at least a 2% sliver so "used a little" is visible.
 */
export function meterFillPercent(
    computed: Pick<IResourceComputed, 'used' | 'limit' | 'ratio'> &
        Partial<Pick<IResourceComputed, 'poolTotal'>>,
): number {
    if (computed.limit === null && computed.poolTotal != null) {
        if (computed.poolTotal <= 0) return 0;
        const percent = (computed.used / computed.poolTotal) * 100;
        return Math.min(100, Math.max(computed.used > 0 ? 2 : 0, percent));
    }
    if (computed.limit === null) return 100;
    return Math.min(100, Math.max(computed.used > 0 ? 2 : 0, computed.ratio * 100));
}

/** Meter fill color by consumption ratio — same thresholds as the badge. */
export function healthBarClass(health: ResourceHealth): string {
    switch (health) {
        case 'critical':
            return 'bg-error-500';
        case 'warning':
            return 'bg-warning-500';
        case 'unlimited':
            return 'bg-primary-300 dark:bg-primary-700';
        case 'healthy':
            return 'bg-primary-500';
    }
}
